import React, { useCallback, useEffect, useMemo } from 'react';
import { MOBILE_BREAKPOINT_THRESHOLD, SxProps } from '@cp/ds/src/theme';
import { AppFormProvider } from '@cp/utils/form';
import { Box } from '@cp/ds/src/components/box';
import { Button } from '@cp/ds/src/components/button';
import { ArrowRightIcon } from '@cp/ds/src/icons';
import { useForm, useFormState } from 'react-hook-form';
import {
  AddressSearchFormField,
  CITY_SEARCH_FORM_FIELD_NAME_DEFAULT,
  CitySearchFormField,
  getMapLocation,
  useCurrentCity,
} from '@cp/entities/location';
import { CityViewModel } from '@common/model/location/cityViewModel';
import { LocationSuggestion } from '@common/model/location/locationSuggestion';
import { ClientRegistrationState } from '@common/model/client/registration/registrationState';
import { GeoPoint } from '@common/model/location/geopoint';
import { useTheme } from '@cp/ds/src/hooks/useTheme';
import { useDeviceLocation } from '@cp/shared/location';
import { useIsMobile } from '@cp/ds/src/hooks/useIsMobile';
import { Container } from '@cp/ds/src/components/container';
import { cx } from '@emotion/css';
import { ClientType } from '@common/model/client/clientType';
import { clientTypeToSuitableCatalogItemType } from '@common/utils/catalogType';
import { PlaceholderedMap } from 'src/shared/ui/map';
import { CLIENT_TYPE_TO_CLIENT_TYPE_NAME, EButtonName, EPageName, useTrackMount, useTracking, withTrackClick } from 'src/shared/tracking';
import { CatalogItemsCounter } from 'src/entities/catalog/item';
import { useMapMarkers } from 'src/shared/ui/map/model/useMapMarkers';
import { useGetLocations } from 'src/entities/catalog';
import { registraionShowAddress, registrationCitySelect, registrationLocationNext, registrationStreetSelect } from '../../lib';
import { StepFooter } from '../stepFooter';
import { StepWrapper } from '../stepWrapper';
import { ClientRegistrationStepProps } from './model';

const NextButton = withTrackClick(Button, registrationLocationNext);

type LocationFormValues = {
  city: CityViewModel | undefined;
  location?: GeoPoint;
  selectedAddress: LocationSuggestion | undefined;
};

export const StepLocation: React.FC<ClientRegistrationStepProps> = ({ onSubmit, model, state }) => {
  useTrackMount(registraionShowAddress);
  const theme = useTheme();
  const isMobile = useIsMobile();
  const deviceLocation = useDeviceLocation();
  const trackCityFilled = useTracking(registrationCitySelect);
  const trackAddressFilled = useTracking(registrationStreetSelect);

  const catalogType = clientTypeToSuitableCatalogItemType(state.clientType || ClientType.Specialist);

  const { currentCity } = useCurrentCity({ allowEmptyValue: true });
  const requiredNames = useMemo(() => model.fields.filter((field) => field.required).map((field) => field.name), [model]);
  const form = useForm<LocationFormValues>({
    defaultValues: { city: currentCity },
    resolver: (values) => {
      const errors = {};
      if (requiredNames.includes('cityId') && !values.city) {
        errors['city'] = { message: 'Выберите город' };
      }
      if (requiredNames.includes('address') && !values.selectedAddress) {
        errors['selectedAddress'] = { message: 'Укажите адрес' };
      }
      return { values, errors };
    },
  });

  const { isValid, isSubmitting } = useFormState({ control: form.control });
  const submitHandler = useCallback(
    (formValues: LocationFormValues) => {
      const values: Partial<ClientRegistrationState> = {};
      if (formValues.city) {
        values.cityId = formValues.city.id;
      }
      if (formValues.selectedAddress) {
        values.address = formValues.selectedAddress.address;
        values.location = formValues.selectedAddress.location;
      }
      if (deviceLocation) {
        values.deviceLocation = deviceLocation.toString();
      }
      return onSubmit(values);
    },
    [onSubmit, deviceLocation],
  );
  const [city, selectedAddress] = form.watch([CITY_SEARCH_FORM_FIELD_NAME_DEFAULT, 'selectedAddress']);
  const mapCenter = useMemo(() => {
    const location = getMapLocation(selectedAddress, city, deviceLocation);
    return location ? GeoPoint.create({ lat: location.lat - 0.1, long: location.long }) : undefined;
  }, [deviceLocation, city, selectedAddress]);

  const locations = useGetLocations(catalogType, state.mainCareerId, city?.id);
  const { markers } = useMapMarkers(locations);

  const mapSx: SxProps = {
    height: 345,
    width: { xs: 1, [MOBILE_BREAKPOINT_THRESHOLD]: 700 },
    borderRadius: { xs: 0, [MOBILE_BREAKPOINT_THRESHOLD]: `${theme.borderRadius.normal}px` },
    overflow: 'hidden',
  };

  const Wrapper = isMobile ? Container : Box;

  useEffect(() => {
    const { invalid, isDirty } = form.getFieldState(CITY_SEARCH_FORM_FIELD_NAME_DEFAULT);
    const value = form.getValues(CITY_SEARCH_FORM_FIELD_NAME_DEFAULT)?.id;
    if (!isDirty || invalid || !value) {
      return;
    }
    trackCityFilled({ city: value });
  }, [city, form, trackCityFilled]);

  useEffect(() => {
    const { invalid, isDirty } = form.getFieldState('selectedAddress');
    const value = form.getValues('selectedAddress');
    if (!isDirty || invalid || !value) {
      return;
    }
    trackAddressFilled();
  }, [selectedAddress, form, trackAddressFilled]);

  return (
    <AppFormProvider form={form}>
      <Box component="form" onSubmit={form.handleSubmit(submitHandler)} sx={{ pt: isMobile ? 4 : 0, display: 'flex', flexGrow: 1 }}>
        <StepWrapper>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Wrapper sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <CitySearchFormField name={CITY_SEARCH_FORM_FIELD_NAME_DEFAULT} placeholder="Город" />
              {requiredNames.includes('address') && (
                <AddressSearchFormField cityName={CITY_SEARCH_FORM_FIELD_NAME_DEFAULT} name="selectedAddress" placeholder="Адрес" />
              )}
            </Wrapper>
            <Box sx={{ position: 'relative' }}>
              <PlaceholderedMap center={mapCenter} fullscreenControl={false} markers={markers} sx={mapSx} zoomControl={false} />
              {city && state.mainCareerId && state.clientType ? (
                <CatalogItemsCounter
                  careerId={state.mainCareerId}
                  cityId={city.id}
                  clientType={state.clientType}
                  sx={{
                    position: 'absolute',
                    left: '50%',
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                  }}
                />
              ) : null}
            </Box>
          </Box>
          <StepFooter>
            <NextButton
              className={cx(
                EButtonName.Next,
                EPageName.Registration,
                model.name,
                CLIENT_TYPE_TO_CLIENT_TYPE_NAME[state.clientType as ClientType],
              )}
              disabled={!isValid || isSubmitting}
              iconRight={<ArrowRightIcon />}
              sx={{ flex: { xs: '1 0 100%', [MOBILE_BREAKPOINT_THRESHOLD]: '0 0 auto' } }}
              type="submit"
            >
              Далее
            </NextButton>
          </StepFooter>
        </StepWrapper>
      </Box>
    </AppFormProvider>
  );
};
